import { Inject, Injectable } from '@angular/core';
import { DeliveryAddress } from '../models/address';
import { UserAddressResponse, UserResponse } from '../models/api-responses';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { combineLatest, map, Observable, of, take } from 'rxjs';
import { User } from '../models/user.model';
import { OrganizationsService } from './organizations.service';
import {
  MenuResponseSize,
  ProductGroupInfoResponse,
  ProductGroupResponse,
  ProductModifierResponse,
  ProductResponse,
  ProductSizeResponse
} from '../models/rpc-response.model';
import {
  OrganizationMetadata,
  Product,
  ProductGroup,
  ProductGroupInfo,
  ProductModifier,
  ProductSize
} from '../models/organization.model';
import { OrderStatus } from 'app/models/order-info.model';
import { DeliveryType } from 'app/models/user-order.model';
import { IOrganizationMetadata, LoyaltySystem } from 'app/models/organizatioMetadata.model';
import { YA_API_KEY_TOKEN } from 'app/tokens/ya-api.token';

@Injectable({
  providedIn: 'root'
})
export class ConverterService {

  constructor(
    @Inject(YA_API_KEY_TOKEN) private readonly apiKey: string,
    private store: Store<AppState>,
    private organizationService: OrganizationsService
  ) {

  }

  // заглушка для поддержки общей логики
  public static convertProductSizeResponse(productSizeResponse: ProductSizeResponse[], sizes: MenuResponseSize[]): ProductSize[] {
    return productSizeResponse.map(value => {
      const menuSize = sizes.find(value1 => value1.id === value.id);
      return {
        id: value.id ?? '',
        name: menuSize ? menuSize.name : '',
        price: value.price
      }
    });
  }

  // заглушка для поддержки общей логики
  public static convertProductGroupInfoResponse(productGroupInfoResponse: ProductGroupInfoResponse): ProductGroupInfo {
    return productGroupInfoResponse;
  }

  public static convertModifierResponse(productModifierResponse: ProductModifierResponse, allSizes: MenuResponseSize[], allProducts: ProductResponse[]): ProductModifier {
    let product = allProducts.find(value => value.id === productModifierResponse.id);
    let name = '';
    let sizes: ProductSize[] = [];
    let defaultPrice = 0;
    if (product) {
      name = product.name;
      sizes = ConverterService.convertProductSizeResponse(product.sizes, allSizes);
      defaultPrice = sizes.length ? sizes[0].price : 0;
    }
    return {
      id: productModifierResponse.id,
      maxAmount: productModifierResponse.maxAmount,
      defaultAmount: productModifierResponse.defaultAmount,
      minAmount: productModifierResponse.minAmount,
      required: productModifierResponse.required,
      name: name,
      sizes: sizes,
      defaultPrice: defaultPrice,
      order: productModifierResponse.order,
    };
  }

  public static convertProductResponse(
    productResponse: ProductResponse,
    allSizes: MenuResponseSize[],
    allProducts: ProductResponse[]
  ): Product {
    return {
      id: productResponse.id,
      name: productResponse.name,
      productGroups: productResponse.productGroups,
      sort: productResponse.sort,
      tags: productResponse.tags,
      description: productResponse.description,
      coverImage: productResponse.imageLinks[0] ?? '',
      imageLinks: productResponse.imageLinks,
      deliveryTerminalId: productResponse.deliveryTerminalId,
      kind: productResponse.kind,
      productGroupInfo: ConverterService.convertProductGroupInfoResponse(productResponse.productGroupInfo),
      sizes: ConverterService.convertProductSizeResponse(productResponse.sizes, allSizes),
      modifiers: productResponse.modifiers.map(
        modifierResponse => ConverterService.convertModifierResponse(modifierResponse, allSizes, allProducts)
      )
    }
  }

  // заглушка для поддержки общей логики
  public static convertProductGroupResponse(productGroupResponse: ProductGroupResponse, allSizes: MenuResponseSize[], allProducts: ProductResponse[]): ProductGroup {
    return {
      id: productGroupResponse.id,
      defaultAmount: productGroupResponse.defaultAmount,
      minAmount: productGroupResponse.minAmount,
      maxAmount: productGroupResponse.maxAmount,
      productGroupInfo: ConverterService.convertProductGroupInfoResponse(productGroupResponse.productGroupInfo),
      modifiers: productGroupResponse.modifiers.map(
        modifierResponse => ConverterService.convertModifierResponse(modifierResponse, allSizes, allProducts)
      ).sort((prev, next) => prev.order - next.order),
    };
  }

  public static convertUserOrderStatusToPassedSteps(status: OrderStatus, deliveryType: DeliveryType): (boolean | null)[] {
    const passedSteps: { [key in OrderStatus]: (boolean | null)[]; } = {
      [OrderStatus.Error]: [],
      [OrderStatus.Draft]: [false, null, null, null],
      [OrderStatus.Canceled]: [],
      [OrderStatus.Created]: [false, null, null, null],
      [OrderStatus.PaymentAwait]: [false, null, null, null],
      [OrderStatus.Accepted]: [true, false, null, null],
      [OrderStatus.Cooking]: [true, false, null, null],
      [OrderStatus.ReadyToShip]: [true, true, false, null],
      [OrderStatus.Shipped]: [true, true, true, false],
      [OrderStatus.Delivered]: [],
      [OrderStatus.Closed]: []
    };
    if (deliveryType === DeliveryType.PICKUP) {
      passedSteps[OrderStatus.ReadyToShip] = [true, true, true, null];
    }

    return passedSteps[status];
  }

  public convertOrganizationMetadata(metadata: IOrganizationMetadata | null | undefined): OrganizationMetadata {
    if (metadata) {
      return {
        yandexMetricId: parseInt(metadata.yandexMetricaId),
        yandexMapsToken: metadata.yandexMapsToken && metadata.yandexMapsToken.length > 0
          ? metadata.yandexMapsToken : this.apiKey,
        discount: metadata.discount
      };
    } else {
      return {
        yandexMetricId: null,
        yandexMapsToken: this.apiKey,
        discount: {
          loyaltySystem: LoyaltySystem.No
        }
      };
    }
  }

  public convertUserAddressResponse(userAddressResponse: UserAddressResponse): Observable<DeliveryAddress> {
    return this.organizationService.getTerminalByPoint(userAddressResponse.id, {
      lat: userAddressResponse.latitude,
      lng: userAddressResponse.longitude,
    }).pipe(
      map(deliveryTerminal => {
        return {
          id: userAddressResponse.id,
          addressId: userAddressResponse.id,
          type: DeliveryType.DELIVERY,
          name: '',
          deliveryPoint: {
            region: userAddressResponse.region,
            street: userAddressResponse.street,
            country: userAddressResponse.country,
            latitude: userAddressResponse.latitude,
            longitude: userAddressResponse.longitude,
            apartment: userAddressResponse.apartment,
            city: userAddressResponse.city,
            description: userAddressResponse.description,
            house: userAddressResponse.house,
          },
          deliveryTerminal: deliveryTerminal
        };
      })
    );
  }

  public convertUserAddressesResponse(userAddressesResponse: UserAddressResponse[]): Observable<DeliveryAddress[]> {
    const userAddresses = userAddressesResponse.map(userAddressResponse => {
      return this.convertUserAddressResponse(userAddressResponse);
    });
    // если массив пустой, то combineLatest не отрабатывает
    return userAddresses.length ? combineLatest(userAddresses) : of([]);
  }

  public convertUserResponse(userResponse: UserResponse): Observable<User> {
    return this.convertUserAddressesResponse(userResponse.userAddresses).pipe(
      take(1),
      map(deliveryAddresses => {
        return {
          lastName: userResponse.lastName,
          firstName: userResponse.firstName,
          email: userResponse.email,
          userName: (userResponse.firstName + ' ' + userResponse.lastName).trim(),
          phone: userResponse.phone,
          deliveryAddresses: deliveryAddresses
        }
      })
    );
  }
}
